/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { useEffect } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useKPIContext } from '@/providers/KpiProvider';
import { convertToUTCDate } from '@/utils/convertToUTCDate';

import { DateRange } from '../display-page/prompts/date-range-picker/calendar-helper';
import {
  getPresetRange,
  usePresets,
} from '../display-page/prompts/date-range-picker/calendar-helper/presets';

const convertDateToUTC = (date: Date): Date => {
  const utcString = convertToUTCDate(date);
  return new Date(utcString!);
};

const formatDateForURL = (date: Date): string => date.toISOString();

const useKPI = (appName?: string, workflowName?: string) => {
  const { fetchSankeyData, data, loading, fetchKPI } = useKPIContext();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const presets = usePresets();

  // Use workflowName if provided, otherwise use appName
  const identifier = workflowName || appName || '';

  const handleDateRangeApply = async (values: { range: DateRange }) => {
    const { range } = values;
    if (!identifier) return;
    if (!range.from && !range.to) {
      const startTime = null;
      const endTime = null;

      const params = new URLSearchParams(searchParams);
      params.set('days', 'all');
      params.delete('start');
      params.delete('end');
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
      const apiRange = {
        from: !startTime ? startTime : convertDateToUTC(startTime),
        to: !endTime ? endTime : convertDateToUTC(endTime),
      };

      // only run their respective api, as per appName or workflowName
      if (workflowName) fetchKPI(identifier, apiRange);
      if (appName) {
        fetchSankeyData(identifier, null, null, apiRange);
      }
    } else if (range.from && range.to) {
      const params = new URLSearchParams(searchParams);
      params.delete('days');
      params.set('start', formatDateForURL(range.from));
      params.set('end', formatDateForURL(range.to));
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
      const apiRange = {
        from: convertDateToUTC(range.from),
        to: convertDateToUTC(range.to),
      };

      if (workflowName) fetchKPI(identifier, apiRange);
      if (appName) {
        fetchSankeyData(identifier, null, null, apiRange);
      }
    }
  };

  // only run for workflow.
  useEffect(() => {
    if (!workflowName) return;

    const daysParam = searchParams.get('days');
    const startParam = searchParams.get('start');
    const endParam = searchParams.get('end');

    let apiRange;

    if (daysParam === 'all') {
      apiRange = {
        from: null,
        to: null,
      };
    } else if (startParam && endParam) {
      apiRange = {
        from: convertDateToUTC(new Date(startParam)),
        to: convertDateToUTC(new Date(endParam)),
      };
    } else {
      const range = getPresetRange('last7', presets);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      const now = new Date();

      apiRange = {
        from: convertDateToUTC(range.from || sevenDaysAgo),
        to: convertDateToUTC(range.to || now),
      };
    }

    fetchKPI(identifier, apiRange);
  }, [identifier, searchParams, fetchKPI]);

  return {
    KPIData: appName && data ? (data[appName] ?? null) : (data ?? null),
    loading,
    handleDateRangeApply,
  };
};

export default useKPI;
